import React, { useMemo, } from 'react';
import { useToggle, useList, } from 'react-use';
import { pick, isEmpty, last, keyBy, sortBy, round, ceil, floor, sumBy, range, difference, } from 'lodash';
import { Input, Button, } from 'reactstrap';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { toast } from 'react-toastify';
import { differenceInMonths, parse, addMonths, format as formatDate, } from 'date-fns';

import firebase from '../firebase';
import { batch, } from '../shared/firebase';
import { integerFormat, floatFormat, } from '../util';
import { fiscalYearOfPeriod, createOperationLogs, } from '../utils';
import { fields, roundingMethods, } from '../shared/models/fixedAmountProcess';
import { inheritTypes, } from '../shared/models/consolidationJournal';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import ModelFormModal from './modals/ModelFormModal';
import ProgressButton from './ProgressButton';
import AddButton from './AddButton';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';
import ExportButton from './ExportButton';

const db = firebase.firestore();

const roundingFunctions = { round, floor, ceil };

const computeEndYearMonth = (startYearMonth, months) => {
  if (!startYearMonth || !months) return null;
  const startDate = parse(startYearMonth, 'yyyyMM', new Date());
  const endDate = addMonths(startDate, months - 1);
  return formatDate(endDate, 'yyyyMM');
};

const computeAmount = (fixedAmountProcess, targetYearMonth, fiscalYear) => {
  const { baseAmount, ratio, startYearMonth, months, roundingMethod = 'round', } = fixedAmountProcess;
  const roundFn = roundingFunctions[roundingMethod] || round;
  const totalAmount = round(baseAmount * ratio, 0);
  const effectiveStartYearMonth = startYearMonth || fiscalYear?.startYearMonth;
  const endYearMonth = computeEndYearMonth(startYearMonth, months);
  const effectiveEndYearMonth = endYearMonth || fiscalYear?.endYearMonth;
  if (!effectiveStartYearMonth || !effectiveEndYearMonth) return { monthlyAmount: 0, cumulativeAmount: 0, totalMonths: 0, currentMonth: 0, isAfterEnd: false, };

  const startDate = parse(effectiveStartYearMonth, 'yyyyMM', new Date());
  const endDate = parse(effectiveEndYearMonth, 'yyyyMM', new Date());
  const targetDate = parse(targetYearMonth, 'yyyyMM', new Date());

  const totalMonths = months || (differenceInMonths(endDate, startDate) + 1);
  const monthlyAmount = roundFn(totalAmount / totalMonths, 0);

  const isAfterEnd = targetYearMonth > effectiveEndYearMonth;
  const calcTargetYearMonth = isAfterEnd ? effectiveEndYearMonth : targetYearMonth;
  const calcTargetDate = isAfterEnd ? endDate : targetDate;

  const currentPeriodStartYearMonth = effectiveStartYearMonth > fiscalYear?.startYearMonth ? effectiveStartYearMonth : fiscalYear?.startYearMonth;
  const currentPeriodEndYearMonth = effectiveEndYearMonth < fiscalYear?.endYearMonth ? effectiveEndYearMonth : fiscalYear?.endYearMonth;
  const currentPeriodStart = parse(currentPeriodStartYearMonth, 'yyyyMM', new Date());
  const currentPeriodEnd = parse(currentPeriodEndYearMonth, 'yyyyMM', new Date());

  const monthsInCurrentPeriod = differenceInMonths(currentPeriodEnd, currentPeriodStart) + 1;
  const monthsFromPeriodStart = isAfterEnd
    ? monthsInCurrentPeriod
    : differenceInMonths(calcTargetDate, currentPeriodStart) + 1;

  const monthsFromStart = differenceInMonths(calcTargetDate, startDate) + 1;
  const isLastMonth = calcTargetYearMonth === effectiveEndYearMonth;

  const cumulativeBeforeThisMonth = monthlyAmount * (monthsFromStart - 1);
  const currentMonthAmount = isLastMonth ? (totalAmount - cumulativeBeforeThisMonth) : monthlyAmount;

  const prevPeriodMonths = effectiveStartYearMonth < fiscalYear?.startYearMonth
    ? differenceInMonths(currentPeriodStart, startDate)
    : 0;
  const prevPeriodCumulative = monthlyAmount * prevPeriodMonths;
  const cumulativeFromStart = cumulativeBeforeThisMonth + currentMonthAmount;
  const cumulativeAmount = cumulativeFromStart - prevPeriodCumulative;

  return { monthlyAmount: isAfterEnd ? 0 : currentMonthAmount, cumulativeAmount, totalMonths, currentMonth: monthsFromPeriodStart, isAfterEnd, };
};

export default function FixedAmountProcesses(props) {
  const { company, period, yearMonth, isLockedMonth, relatedCompanies, allConsolidationAccountItems, allConsolidationSegments, allConsolidationJournalTypes, consolidationJournalsRef, customChangeFields, ordersByModelType, } = props;
  const companiesById = keyBy(relatedCompanies, 'id');
  const consolidationAccountItemsById = keyBy(allConsolidationAccountItems, 'id');
  const consolidationSegmentsById = keyBy(allConsolidationSegments, 'id');
  const consolidationJournalTypesById = keyBy(allConsolidationJournalTypes, 'id');
  const [isDragging, toggleDragging] = useToggle(false);
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds, }] = useList([]);
  const unselectId = _ => removeSelectedIdAt(selectedIds.indexOf(_));
  const fixedAmountProcessesRef = company.ref.collection('fixedAmountProcesses');
  const { items: fixedAmountProcesses, isLoading, } = useCollectionSubscription(fixedAmountProcessesRef.orderBy('index'), [company], { detail: true });
  const fiscalYear = useMemo(_ => {
    const fy = fiscalYearOfPeriod(period, company.fiscalYears);
    if (!fy) return null;
    return {
      ...fy,
      startYearMonth: fy.start_date.replace(/-/g, '').slice(0, 6),
      endYearMonth: fy.end_date.replace(/-/g, '').slice(0, 6),
    };
  }, [period, company.fiscalYears]);
  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMoveImmutable(fixedAmountProcesses, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => {
      batch.update(fixedAmountProcessesRef.doc(id), { index });
    });
  };
  const onClickApply = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length}件の定額処理を計上しますか？`)) return;

    const selectedProcesses = fixedAmountProcesses.filter(_ => selectedIds.includes(_.id) && isInRange(_));
    const processesWithRef = selectedProcesses.map(fixedAmountProcess => ({ fixedAmountProcess, ref: consolidationJournalsRef.doc() }));
    await batch(db, processesWithRef, (batch, { fixedAmountProcess, ref }) => {
      const { name, type, inheritType, debitCompanyId, debitItemId, debitSegmentId, debitIsIntersegment, debitChangeType, creditCompanyId, creditItemId, creditSegmentId, creditIsIntersegment, creditChangeType, } = fixedAmountProcess;
      const { cumulativeAmount } = computeAmount(fixedAmountProcess, yearMonth, fiscalYear);
      batch.set(ref, {
        type,
        isOpening: false,
        inheritType: inheritType || 'none',
        description: `定額処理: ${name} (${yearMonth.slice(0, 4)}/${yearMonth.slice(4)})`,
        items: [{
          debitCompanyId,
          debitItemId,
          debitSegmentId,
          debitIsIntersegment: debitIsIntersegment || false,
          debitChangeType,
          debitAmount: cumulativeAmount,
          creditCompanyId,
          creditItemId,
          creditSegmentId,
          creditIsIntersegment: creditIsIntersegment || false,
          creditChangeType,
          creditAmount: cumulativeAmount,
        }],
        period,
        yearMonth,
        fixedAmountProcessId: fixedAmountProcess.id,
        createdAt: new Date(),
      });
    });
    await createOperationLogs(company, 'consolidationJournal', 'createConsolidationJournal', processesWithRef.map(_ => ({ yearMonth, consolidationJournalId: _.ref.id, fixedAmountProcessId: _.fixedAmountProcess.id })));
    toast.success(`${selectedProcesses.length}件の仕訳を作成しました`);
    setSelectedIds([]);
  };
  const isInRange = (fixedAmountProcess) => {
    const { startYearMonth, months, } = fixedAmountProcess;
    const endYearMonth = computeEndYearMonth(startYearMonth, months);
    const effectiveStart = startYearMonth || fiscalYear?.startYearMonth;
    const effectiveEnd = endYearMonth || fiscalYear?.endYearMonth;
    if (effectiveStart && yearMonth < effectiveStart) return false;
    if (effectiveEnd && effectiveEnd < fiscalYear?.startYearMonth) return false;
    if (effectiveStart && effectiveStart > fiscalYear?.endYearMonth) return false;
    return true;
  };
  const inRangeProcesses = fixedAmountProcesses.filter(_ => isInRange(_));
  const isAllSelected = inRangeProcesses.length > 0 && difference(inRangeProcesses.map(_ => _.id), selectedIds).length === 0;

  const generateAmortizationSchedule = (fixedAmountProcess) => {
    const { name, baseAmount, ratio, startYearMonth, months, roundingMethod = 'round', } = fixedAmountProcess;
    const roundFn = roundingFunctions[roundingMethod] || round;
    const totalAmount = round(baseAmount * ratio, 0);
    const endYearMonth = computeEndYearMonth(startYearMonth, months);
    if (!startYearMonth || !months) return [];

    const startDate = parse(startYearMonth, 'yyyyMM', new Date());
    const totalMonths = months;
    const monthlyAmount = roundFn(totalAmount / totalMonths, 0);

    const sortedFiscalYears = [...(company.fiscalYears || [])].sort((a, b) => a.start_date.localeCompare(b.start_date));
    const lastFiscalYear = last(sortedFiscalYears);
    const lastFiscalEndMonth = lastFiscalYear ? parseInt(lastFiscalYear.end_date.slice(5, 7), 10) : 3;

    const getFiscalYearEnd = (ym) => {
      const targetDate = parse(ym, 'yyyyMM', new Date());
      const fy = sortedFiscalYears.find(_ => {
        const fyStart = parse(_.start_date.replace(/-/g, '').slice(0, 6), 'yyyyMM', new Date());
        const fyEnd = parse(_.end_date.replace(/-/g, '').slice(0, 6), 'yyyyMM', new Date());
        return targetDate >= fyStart && targetDate <= fyEnd;
      });
      if (fy) return fy.end_date.replace(/-/g, '').slice(0, 6);

      const year = parseInt(ym.slice(0, 4), 10);
      const month = parseInt(ym.slice(4, 6), 10);
      const fyEndYear = month <= lastFiscalEndMonth ? year : year + 1;
      return `${fyEndYear}${String(lastFiscalEndMonth).padStart(2, '0')}`;
    };

    const rows = [];
    let cumulativeFromStart = 0;
    let periodCumulative = 0;
    let currentFiscalYearEnd = null;

    range(0, totalMonths).forEach((i) => {
      const currentDate = addMonths(startDate, i);
      const currentYearMonth = formatDate(currentDate, 'yyyyMM');
      const fiscalYearEnd = getFiscalYearEnd(currentYearMonth);

      if (currentFiscalYearEnd !== null && currentFiscalYearEnd !== fiscalYearEnd) {
        periodCumulative = 0;
      }
      currentFiscalYearEnd = fiscalYearEnd;

      const isLastMonth = i === totalMonths - 1;
      const currentMonthAmount = isLastMonth ? (totalAmount - cumulativeFromStart) : monthlyAmount;
      cumulativeFromStart += currentMonthAmount;
      periodCumulative += currentMonthAmount;
      const remainingBalance = totalAmount - cumulativeFromStart;

      rows.push({
        年月: `${currentYearMonth.slice(0, 4)}/${currentYearMonth.slice(4)}`,
        当月計上額: currentMonthAmount,
        当期累計額: periodCumulative,
        通期累計額: cumulativeFromStart,
        期末残高: remainingBalance,
      });
    });

    const lastRowYearMonth = formatDate(addMonths(startDate, totalMonths - 1), 'yyyyMM');
    const lastFiscalYearEnd = getFiscalYearEnd(lastRowYearMonth);
    if (lastRowYearMonth !== lastFiscalYearEnd) {
      const lastDate = parse(lastRowYearMonth, 'yyyyMM', new Date());
      const fiscalEndDate = parse(lastFiscalYearEnd, 'yyyyMM', new Date());
      const remainingMonthsInFiscalYear = differenceInMonths(fiscalEndDate, lastDate);
      range(1, remainingMonthsInFiscalYear + 1).forEach((i) => {
        const currentDate = addMonths(lastDate, i);
        const currentYearMonth = formatDate(currentDate, 'yyyyMM');
        rows.push({
          年月: `${currentYearMonth.slice(0, 4)}/${currentYearMonth.slice(4)}`,
          当月計上額: 0,
          当期累計額: periodCumulative,
          通期累計額: cumulativeFromStart,
          期末残高: 0,
        });
      });
    }

    return rows;
  };

  return (
    <div className="fixed-amount-processes">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          {selectedIds.length > 0 && (
            <>
              <span>{selectedIds.length}件選択中</span>
              <ProgressButton color="success" process={onClickApply} disabled={isLockedMonth}>
                <span className="fas fa-check mr-1" />
                計上
              </ProgressButton>
              <Button color="secondary" onClick={_ => setSelectedIds([])}>
                選択解除
              </Button>
            </>
          )}
        </div>
        <AddButton disabled={isLoading} itemRef={fixedAmountProcessesRef.doc()} processValues={_ => ({ ..._, index: fixedAmountProcesses.length, })} FormModal={ModelFormModal} formProps={{ title: '定額処理マスタ追加', fields: fields({ company, companies: relatedCompanies, consolidationSegments: allConsolidationSegments, consolidationAccountItems: allConsolidationAccountItems, allConsolidationJournalTypes, customChangeFields, ordersByModelType, }), }} />
      </div>
      <div>
        <table className="table sticky-table">
          <thead className="thead-light text-center">
            <tr>
              <th style={{ width: 30 }}>
                <Input
                  type="checkbox"
                  className="position-relative m-0"
                  checked={isAllSelected}
                  onChange={_ => isAllSelected ? setSelectedIds([]) : setSelectedIds(inRangeProcesses.map(_ => _.id))}
                />
              </th>
              <th></th>
              <th style={{ minWidth: 120 }}>名称</th>
              <th style={{ minWidth: 120 }}>金額</th>
              <th style={{ minWidth: 100 }}>期間</th>
              <th style={{ minWidth: 120 }}>計上額</th>
              <th style={{ minWidth: 80 }}>種別/引継</th>
              <th style={{ minWidth: 180 }}>借方</th>
              <th style={{ minWidth: 180 }}>貸方</th>
              <th></th>
            </tr>
          </thead>
          <Container
            dragHandleSelector=".drag-handle"
            onDrop={onDrop}
            onDragStart={_ => toggleDragging(true)}
            onDragEnd={_ => toggleDragging(false)}
            dropPlaceholder={{ style: { background: 'eee', } }}
            render={(ref) => (
              <tbody ref={ref}>
                {
                  fixedAmountProcesses.map((fixedAmountProcess) => {
                    const { id, ref, name, baseAmount, ratio, startYearMonth, months, roundingMethod = 'round', type, inheritType, debitCompanyId, debitItemId, debitSegmentId, debitIsIntersegment, debitChangeType, creditCompanyId, creditItemId, creditSegmentId, creditIsIntersegment, creditChangeType, } = fixedAmountProcess;
                    const endYearMonth = computeEndYearMonth(startYearMonth, months);
                    const debitCompany = companiesById[debitCompanyId];
                    const debitItem = consolidationAccountItemsById[debitItemId];
                    const debitSegment = consolidationSegmentsById[debitSegmentId];
                    const creditCompany = companiesById[creditCompanyId];
                    const creditItem = consolidationAccountItemsById[creditItemId];
                    const creditSegment = consolidationSegmentsById[creditSegmentId];
                    const totalAmount = round(baseAmount * ratio, 0);
                    const inRange = isInRange(fixedAmountProcess);
                    const { monthlyAmount, cumulativeAmount, totalMonths, currentMonth, isAfterEnd, } = computeAmount(fixedAmountProcess, yearMonth, fiscalYear);

                    return (
                      <Draggable
                        key={id}
                        render={() => (
                          <tr style={{ display: !isDragging && 'table-row', ...(!inRange && { background: '#e2e3e5' }), }}>
                            <td style={{ width: 30 }}>
                              {inRange && (
                                <Input
                                  type="checkbox"
                                  className="position-relative m-0"
                                  checked={selectedIds.includes(id)}
                                  onChange={_ => selectedIds.includes(id) ? unselectId(id) : selectId(id)}
                                />
                              )}
                            </td>
                            <td style={{ width: 30 }}>
                              <div className="drag-handle text-muted cursor-pointer">
                                <span className="fas fa-grip-vertical" />
                              </div>
                            </td>
                            <td>{name}</td>
                            <td className="small text-right">
                              <div>{integerFormat(baseAmount)} × {floatFormat(ratio, 6)}</div>
                              <div className="font-weight-bold">= {integerFormat(totalAmount)}</div>
                            </td>
                            <td className="small">
                              {startYearMonth && <div>開始: {startYearMonth.slice(0, 4)}/{startYearMonth.slice(4)}</div>}
                              {endYearMonth && <div>終了: {endYearMonth.slice(0, 4)}/{endYearMonth.slice(4)}</div>}
                              {months && <div className="text-muted">{months}ヶ月</div>}
                              {inRange && isAfterEnd && <div className="badge badge-warning">期間終了</div>}
                            </td>
                            <td className="text-right">
                              {inRange && (
                                <>
                                  {!isAfterEnd && <div className="small text-muted">当月 {integerFormat(monthlyAmount)}</div>}
                                  <div className="font-weight-bold">当期累計 {integerFormat(cumulativeAmount)}</div>
                                </>
                              )}
                            </td>
                            <td className="small">
                              <div>{consolidationJournalTypesById[type]?.name}</div>
                              {inheritType !== 'none' && <div className="text-muted">{inheritTypes[inheritType]?.label}</div>}
                              {roundingMethod !== 'round' && <div className="text-muted">{roundingMethods[roundingMethod]?.label}</div>}
                            </td>
                            <td className="small">
                              <div>{debitCompany?.display_name}</div>
                              <div>{debitItem?.name}</div>
                              <div className="text-muted">{debitSegment?.name}</div>
                              {debitIsIntersegment && <span className="badge badge-secondary">セグメント間</span>}
                            </td>
                            <td className="small">
                              <div>{creditCompany?.display_name}</div>
                              <div>{creditItem?.name}</div>
                              <div className="text-muted">{creditSegment?.name}</div>
                              {creditIsIntersegment && <span className="badge badge-secondary">セグメント間</span>}
                            </td>
                            <td className="text-nowrap text-right">
                              <div className="d-flex gap-1 justify-content-end">
                                <EditButton size="sm" itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '定額処理マスタ編集', fields: fields({ company, companies: relatedCompanies, consolidationSegments: allConsolidationSegments, consolidationAccountItems: allConsolidationAccountItems, allConsolidationJournalTypes, customChangeFields, ordersByModelType, keptChangeTypes: [debitChangeType, creditChangeType].filter(_ => _), }), }} />
                                <DeleteButton size="sm" item={fixedAmountProcess} itemRef={ref} />
                              </div>
                              <div className="mt-1 d-flex gap-1 justify-content-end">
                                <ExportButton size="sm" label="償却予定表" screenName={`償却予定表_${name}`} rows={_ => generateAmortizationSchedule(fixedAmountProcess)} disabled={!startYearMonth || !months} />
                              </div>
                            </td>
                          </tr>
                        )}
                      />
                    );
                  })
                }
              </tbody>
            )}
          />
        </table>
      </div>
    </div>
  );
}
